import Layout from "@/components/LandingPage/Layout";
import { Typography, Divider } from "antd";
import Link from "next/link";
import React, { useState, useEffect } from "react";

const { Title, Paragraph, Text } = Typography;

const RefundPolicy = () => {
  const [activeSection, setActiveSection] = useState("");

  const tableOfContents = [
    { key: "overview", title: "Refund Policy Overview", href: "#overview" },
    {
      key: "subscription-refunds",
      title: "Subscription Refunds",
      href: "#subscription-refunds",
    },
    {
      key: "cancellation-policy",
      title: "Cancellation Policy",
      href: "#cancellation-policy",
    },
    {
      key: "refund-eligibility",
      title: "Refund Eligibility",
      href: "#refund-eligibility",
    },
    {
      key: "refund-process",
      title: "Refund Process",
      href: "#refund-process",
    },
    {
      key: "processing-time",
      title: "Processing Time",
      href: "#processing-time",
    },
    {
      key: "non-refundable-items",
      title: "Non-Refundable Items",
      href: "#non-refundable-items",
    },
    {
      key: "partial-refunds",
      title: "Partial Refunds",
      href: "#partial-refunds",
    },
    {
      key: "technical-issues",
      title: "Technical Issues",
      href: "#technical-issues",
    },
    {
      key: "dispute-resolution",
      title: "Dispute Resolution",
      href: "#dispute-resolution",
    },
    {
      key: "modifications",
      title: "Policy Modifications",
      href: "#modifications",
    },
    { key: "contact", title: "Contact Us", href: "#contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map((item) => item.key);
      const headerHeight = document.querySelector("header")?.offsetHeight || 80;
      const scrollPosition = window.scrollY + headerHeight + 100; // Increased offset for better detection
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Check if we're near the bottom of the page
      if (window.scrollY + windowHeight >= documentHeight - 100) {
        // If near bottom, set the last section as active
        setActiveSection(sections[sections.length - 1]);
        return;
      }

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    // Initial call to set the active section on load
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Layout>
      <section className="py-10 bg-gray-50 min-h-screen">
        <div className="w-full mx-auto px-4 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <Title
              style={{ color: "#2D68C4" }}
              className="!text-[28px] sm:!text-[36px] md:!text-[42px] !mb-4"
            >
              Refund Policy
            </Title>
            <Text className="text-lg text-gray-600">
              Understanding our refund and cancellation procedures
            </Text>
            <div className="mt-4">
              <Text className="text-gray-500">Last Updated: 01/01/2025</Text>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="flex flex-col lg:flex-row gap-8 max-w-7xl mx-auto">
            {/* Table of Contents - Sidebar */}
            <div className="lg:w-1/4">
              <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-24">
                <Title level={4} className="!mb-4 !text-[#2D68C4]">
                  Table of Contents
                </Title>
                <div className="space-y-2">
                  {tableOfContents.map((item, index) => (
                    <a
                      key={item.key}
                      href={item.href}
                      className={`block py-2 px-3 rounded-md text-sm transition-colors duration-200 ${
                        activeSection === item.key
                          ? "bg-blue-50 text-[#2D68C4] font-medium border-l-3 border-[#2D68C4]"
                          : "text-gray-600 hover:text-[#2D68C4] hover:bg-gray-50"
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        // Immediately set the active section
                        setActiveSection(item.key);
                        const targetElement = document.getElementById(item.key);
                        if (targetElement) {
                          const headerHeight =
                            document.querySelector("header")?.offsetHeight ||
                            80;
                          window.scrollTo({
                            top: targetElement.offsetTop - headerHeight - 20,
                            behavior: "smooth",
                          });
                        }
                      }}
                    >
                      {item.key === "overview"
                        ? item.title
                        : `${index}. ${item.title}`}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:w-3/4">
              <div className="bg-white rounded-lg shadow-sm border p-8 space-y-8">
                {/* Overview */}
                <div id="overview">
                  <Paragraph className="text-md leading-relaxed mb-6">
                    At StedPrep, we strive to provide exceptional educational
                    services and customer satisfaction. This Refund Policy
                    outlines the terms and conditions under which refunds may be
                    requested and processed for our ISEE preparation platform.
                  </Paragraph>

                  <Paragraph className="text-md leading-relaxed mb-6">
                    By subscribing to our services, you acknowledge that you
                    have read, understood, and agree to be bound by this Refund
                    Policy in conjunction with our Terms and Conditions.
                  </Paragraph>
                </div>

                <Divider />

                {/* Section 1 */}
                <div id="subscription-refunds">
                  <Title
                    level={2}
                    style={{ color: "#2D68C4" }}
                    className="!mb-6 !text-xl"
                  >
                    1. Subscription Refunds
                  </Title>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    1.1. 7-Day Money-Back Guarantee
                  </Title>
                  <Paragraph className="mb-6">
                    We offer a 7-day money-back guarantee for new subscribers.
                    If you are not satisfied with our service within the first 7
                    days of your subscription, you may request a full refund.
                  </Paragraph>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    1.2. Monthly Subscriptions
                  </Title>
                  <Paragraph className="mb-6">
                    Monthly subscriptions are eligible for refund only within
                    the first 7 days of the billing cycle. After this period, no
                    refunds will be provided for the current month.
                  </Paragraph>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    1.3. Annual Subscriptions
                  </Title>
                  <Paragraph className="mb-6">
                    Annual subscriptions may be refunded on a pro-rated basis
                    within the first 30 days of purchase. After 30 days, annual
                    subscriptions are non-refundable.
                  </Paragraph>
                </div>

                <Divider />

                {/* Section 2 */}
                <div id="cancellation-policy">
                  <Title
                    level={2}
                    style={{ color: "#2D68C4" }}
                    className="!mb-6 !text-xl"
                  >
                    2. Cancellation Policy
                  </Title>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    2.1. How to Cancel
                  </Title>
                  <Paragraph className="mb-4">
                    You may cancel your subscription at any time through:
                  </Paragraph>
                  <ul className="ml-6 space-y-2 mb-6 list-disc">
                    <li>Your account settings dashboard</li>
                    <li>Contacting our customer support team</li>
                    <li>
                      Sending an email to{" "}
                      <Link
                        target="_blank"
                        href="https://mail.google.com/mail/?view=cm&to=hello@stedprep.com"
                        className="hover:underline hover:underline-offset-4 text-[#2D68C4]"
                      >
                        hello@stedprep.com
                      </Link>
                    </li>
                  </ul>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    2.2. Effective Date
                  </Title>
                  <Paragraph className="mb-6">
                    Cancellations take effect at the end of your current billing
                    cycle. You will retain access to all features until that
                    date.
                  </Paragraph>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    2.3. No Cancellation Fees
                  </Title>
                  <Paragraph className="mb-6">
                    We do not charge any fees for canceling your subscription.
                  </Paragraph>
                </div>

                <Divider />

                {/* Section 3 */}
                <div id="refund-eligibility">
                  <Title
                    level={2}
                    style={{ color: "#2D68C4" }}
                    className="!mb-6 !text-xl"
                  >
                    3. Refund Eligibility
                  </Title>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    3.1. Eligible Circumstances
                  </Title>
                  <Paragraph className="mb-4">
                    Refunds may be considered in the following situations:
                  </Paragraph>
                  <ul className="ml-6 space-y-2 mb-6 list-disc">
                    <li>Technical issues preventing platform access</li>
                    <li>Billing errors or duplicate charges</li>
                    <li>Service not meeting advertised features</li>
                    <li>Unauthorized charges</li>
                    <li>Platform downtime exceeding 48 hours</li>
                  </ul>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    3.2. Required Documentation
                  </Title>
                  <Paragraph className="mb-4">
                    Refund requests must include:
                  </Paragraph>
                  <ul className="ml-6 space-y-2 mb-6 list-disc">
                    <li>Account email address</li>
                    <li>Transaction ID or receipt</li>
                    <li>Detailed reason for refund request</li>
                    <li>Supporting documentation (if applicable)</li>
                  </ul>
                </div>

                <Divider />

                {/* Section 4 */}
                <div id="refund-process">
                  <Title
                    level={2}
                    style={{ color: "#2D68C4" }}
                    className="!mb-6 !text-xl"
                  >
                    4. Refund Process
                  </Title>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    4.1. How to Request
                  </Title>
                  <Paragraph className="mb-4">To request a refund:</Paragraph>
                  <ol className="ml-6 space-y-2 mb-6 list-decimal">
                    <li>
                      Email{" "}
                      <Link
                        target="_blank"
                        href="https://mail.google.com/mail/?view=cm&to=hello@stedprep.com"
                        className="hover:underline hover:underline-offset-4 text-[#2D68C4]"
                      >
                        hello@stedprep.com
                      </Link>{" "}
                      with "Refund Request" in the subject line
                    </li>
                    <li>Include all required documentation</li>
                    <li>Provide a detailed explanation of your request</li>
                    <li>Wait for our response within 2-3 business days</li>
                  </ol>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    4.2. Review Process
                  </Title>
                  <Paragraph className="mb-6">
                    All refund requests are reviewed individually. We may
                    contact you for additional information or to offer
                    alternative solutions before processing a refund.
                  </Paragraph>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    4.3. Approval Notification
                  </Title>
                  <Paragraph className="mb-6">
                    You will receive email notification regarding the status of
                    your refund request within 5 business days of submission.
                  </Paragraph>
                </div>

                <Divider />

                {/* Section 5 */}
                <div id="processing-time">
                  <Title
                    level={2}
                    style={{ color: "#2D68C4" }}
                    className="!mb-6 !text-xl"
                  >
                    5. Processing Time
                  </Title>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    5.1. Credit Card Refunds
                  </Title>
                  <Paragraph className="mb-6">
                    Approved refunds to credit cards typically take 5-10
                    business days to appear on your statement, depending on your
                    card issuer.
                  </Paragraph>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    5.2. PayPal Refunds
                  </Title>
                  <Paragraph className="mb-6">
                    PayPal refunds are usually processed within 1-2 business
                    days and will appear in your PayPal account immediately.
                  </Paragraph>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    5.3. Bank Transfer Refunds
                  </Title>
                  <Paragraph className="mb-6">
                    Bank transfer refunds may take 3-7 business days to process,
                    depending on your financial institution.
                  </Paragraph>
                </div>

                <Divider />

                {/* Section 6 */}
                <div id="non-refundable-items">
                  <Title
                    level={2}
                    style={{ color: "#2D68C4" }}
                    className="!mb-6 !text-xl"
                  >
                    6. Non-Refundable Items
                  </Title>

                  <Paragraph className="mb-4">
                    The following items are not eligible for refunds:
                  </Paragraph>
                  <ul className="ml-6 space-y-2 mb-6 list-disc">
                    <li>Subscriptions used beyond the refund period</li>
                    <li>Completed diagnostic assessments</li>
                    <li>Downloaded study materials</li>
                    <li>One-time consultation fees</li>
                    <li>Third-party service charges</li>
                    <li>
                      Promotional or discounted subscriptions (unless specified)
                    </li>
                  </ul>
                </div>

                <Divider />

                {/* Section 7 */}
                <div id="partial-refunds">
                  <Title
                    level={2}
                    style={{ color: "#2D68C4" }}
                    className="!mb-6 !text-xl"
                  >
                    7. Partial Refunds
                  </Title>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    7.1. Pro-Rated Refunds
                  </Title>
                  <Paragraph className="mb-6">
                    In certain circumstances, we may offer pro-rated refunds
                    based on unused service time. This is calculated from the
                    date of the refund request to the end of the billing period.
                  </Paragraph>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    7.2. Service Interruption Refunds
                  </Title>
                  <Paragraph className="mb-6">
                    If our service is unavailable for extended periods due to
                    technical issues, we may provide partial refunds or service
                    credits at our discretion.
                  </Paragraph>
                </div>

                <Divider />

                {/* Section 8 */}
                <div id="technical-issues">
                  <Title
                    level={2}
                    style={{ color: "#2D68C4" }}
                    className="!mb-6 !text-xl"
                  >
                    8. Technical Issues
                  </Title>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    8.1. Platform Accessibility
                  </Title>
                  <Paragraph className="mb-6">
                    If you experience technical difficulties accessing our
                    platform, please contact our support team first. We will
                    work to resolve the issue before considering refund options.
                  </Paragraph>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    8.2. Device Compatibility
                  </Title>
                  <Paragraph className="mb-6">
                    Refunds due to device compatibility issues will only be
                    considered if the device meets our published system
                    requirements and the issue cannot be resolved through
                    technical support.
                  </Paragraph>
                </div>

                <Divider />

                {/* Section 9 */}
                <div id="dispute-resolution">
                  <Title
                    level={2}
                    style={{ color: "#2D68C4" }}
                    className="!mb-6 !text-xl"
                  >
                    9. Dispute Resolution
                  </Title>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    9.1. Internal Resolution
                  </Title>
                  <Paragraph className="mb-6">
                    We encourage customers to contact us directly to resolve any
                    billing disputes before initiating chargebacks with their
                    financial institution.
                  </Paragraph>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    9.2. Chargeback Policy
                  </Title>
                  <Paragraph className="mb-6">
                    Initiating a chargeback without first contacting us may
                    result in immediate account suspension. We will provide all
                    necessary documentation to dispute invalid chargebacks.
                  </Paragraph>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    9.3. Escalation Process
                  </Title>
                  <Paragraph className="mb-6">
                    If you are unsatisfied with our initial response to your
                    refund request, you may escalate the matter to our
                    management team by emailing support@stedprep.com with
                    "Escalation Request" in the subject line.
                  </Paragraph>
                </div>

                <Divider />

                {/* Section 10 */}
                <div id="modifications">
                  <Title
                    level={2}
                    style={{ color: "#2D68C4" }}
                    className="!mb-6 !text-xl"
                  >
                    10. Policy Modifications
                  </Title>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    10.1. Updates and Changes
                  </Title>
                  <Paragraph className="mb-6">
                    We reserve the right to modify this Refund Policy at any
                    time. Changes will be effective immediately upon posting on
                    our website.
                  </Paragraph>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    10.2. Notification of Changes
                  </Title>
                  <Paragraph className="mb-6">
                    Significant changes to this policy will be communicated to
                    active subscribers via email at least 30 days before the
                    changes take effect.
                  </Paragraph>

                  <Title level={3} className="!text-lg !mt-6 !mb-4">
                    10.3. Grandfathering
                  </Title>
                  <Paragraph className="mb-6">
                    Existing subscribers will be subject to the refund policy
                    that was in effect at the time of their subscription, unless
                    they agree to new terms.
                  </Paragraph>
                </div>

                <Divider />

                {/* Section 11 */}
                <div id="contact">
                  <Title
                    level={2}
                    style={{ color: "#2D68C4" }}
                    className="!mb-6 !text-xl"
                  >
                    11. Contact Us
                  </Title>
                  <Paragraph className="mb-4">
                    For refund requests, billing questions, or policy
                    clarifications, please contact us:
                  </Paragraph>
                  <ul className="ml-6 space-y-2 mb-6 list-disc">
                    <li>
                      <strong>Email:</strong>{" "}
                      <Link
                        target="_blank"
                        href="https://mail.google.com/mail/?view=cm&to=hello@stedprep.com"
                        className="hover:underline hover:underline-offset-4 text-[#2D68C4]"
                      >
                        hello@stedprep.com
                      </Link>
                    </li>
                    <li>
                      <strong>Support Email:</strong> support@stedprep.com
                    </li>
                    <li>
                      <strong>Phone:</strong> 1-800-STEDPREP (1-800-783-3773)
                    </li>
                    <li>
                      <strong>Business Hours:</strong> Monday - Friday, 9:00 AM
                      - 6:00 PM PST
                    </li>
                    <li>
                      <strong>Mailing Address:</strong> StedPrep Inc., 123
                      Education Lane, Suite 456, San Francisco, CA 94102
                    </li>
                  </ul>

                  <Paragraph className="mb-6">
                    We strive to respond to all refund requests within 2-3
                    business days. For urgent matters, please call our support
                    line during business hours.
                  </Paragraph>
                </div>

                <Divider className="mb-0" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RefundPolicy;
